import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { Box, IconButton, Paper, Stack } from '@mui/material'
import { ItemOption } from 'components/FormInputSelect'
import FormInputDatePicker from 'components/FormInputDatePicker'
import React, { useEffect } from 'react'
import { Path, UseFieldArrayRemove, useFormContext } from 'react-hook-form'

import { FormInputAutoComplete } from 'components/FormInputAutoComplete'
import { useCreasUsuario } from 'pages/SearchCreas/hook/creas-usuario.hook'
import { Funcionalidades } from 'utils/helpers/funcionalidades'
import { useAuthContext } from 'contexts/auth.context'
import { CreasResponse } from 'types/creas.type'
import { addressInit } from 'types/address.type'
import { useAddress } from 'hooks/address.hook'
import { useHearingIdentificationBlockFieldsContext } from 'contexts/hearing-identification-block-fields.context'
import {
  MeasureStatus,
  MeasureStatusLaPsc,
  SocioeducationMeasureProceduralDataType,
} from 'types/socioeducation-measure-procedural-data.type'
import Label from 'components/Label'
import { FormInputFile } from 'components/FormInputFile'

type DocumentItemProps = {
  index?: number
  typeOptions: ItemOption[]
  typeOptionsLaPsc: ItemOption[]
  onRemove?: UseFieldArrayRemove
  isRemovable?: boolean
  disabled?: boolean
}

export const StatusMeasureItem: React.FC<DocumentItemProps> = ({
  typeOptions,
  typeOptionsLaPsc,
  isRemovable,
  disabled,
}) => {
  const { control, watch, clearErrors, setValue, setError } =
    useFormContext<SocioeducationMeasureProceduralDataType>()

  const { handleHasPermission } = useAuthContext()
  const { creas } = useCreasUsuario()

  const doNotHasEditPermission = !handleHasPermission(Funcionalidades.meioAberto.editar)

  const tipoSocioeducativaValue = watch(`tipoMedidaSocioeducativa`)
  const measureStatusValue = watch(
    tipoSocioeducativaValue === 'LA_PSC'
      ? `medidaSocioeducativa.statusMedidaLaPsc`
      : `medidaSocioeducativa.statusMedida`,
  )
  const measureStatusValueLP = watch(`medidaSocioeducativa.statusMedida`)
  const measureStatusValueLaPsc = watch(`medidaSocioeducativa.statusMedidaLaPsc`)

  const measureStatusLaDTO = watch(`medidaSocioeducativa.la`)
  const measureStatusPscDTO = watch(`medidaSocioeducativa.psc`)
  const measureStatusLaPscDTO = watch(`medidaSocioeducativa.laPsc`)

  const idEstadoWatchLa = watch(`medidaSocioeducativa.la.estadoMoradiaDeclarado.id`)
  const idEstadoWatchPsc = watch(`medidaSocioeducativa.psc.estadoMoradiaDeclarado.id`)
  const idEstadoWatchLaPsc = watch(`medidaSocioeducativa.laPsc.estadoMoradiaDeclarado.id`)

  const nameTipoSocioeducativa =
    tipoSocioeducativaValue === 'LA'
      ? 'la'
      : tipoSocioeducativaValue === 'PSC'
      ? 'psc'
      : tipoSocioeducativaValue === 'LA_PSC'
      ? 'laPsc'
      : ''

  const selectedWatch =
    tipoSocioeducativaValue === 'LA_PSC'
      ? watch(`medidaSocioeducativa.laPsc.creasTransferido`)
      : tipoSocioeducativaValue === 'LA'
      ? watch(`medidaSocioeducativa.la.creasTransferido`)
      : watch(`medidaSocioeducativa.psc.creasTransferido`)

  const { isBlockedFields } = useHearingIdentificationBlockFieldsContext()

  const nameMap: Record<MeasureStatus | MeasureStatusLaPsc, string> = {
    [MeasureStatus.CUMPRINDO]: 'Cumprindo',
    [MeasureStatus.DESCUMPRINDO]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataInicioDescumprimento`,
    [MeasureStatus.EXTINTA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSentencaExtincao`,
    [MeasureStatus.OBITO ||
    MeasureStatusLaPsc.OBITO]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorObito`,
    [MeasureStatus.SUSPENSA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataSentencaSuspensao`,
    [MeasureStatus.TRANSFERENCIA_CREAS ||
    MeasureStatusLaPsc.TRANSFERENCIA_CREAS]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataTransferenciaCreas`,
    [MeasureStatus.TRANSFERENCIA_COMARCA ||
    MeasureStatusLaPsc.TRANSFERENCIA_COMARCA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorTransferenciaComarca`,
    [MeasureStatus.SUBSTITUICAO_MEDIDA ||
    MeasureStatusLaPsc.SUBSTITUICAO_MEDIDA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSubstituicaoMedida`,
    [MeasureStatus.IMPOSSIBILITADO_NOVA_APREENSAO ||
    MeasureStatusLaPsc.IMPOSSIBILITADO_NOVA_APREENSAO]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorNovaApreensao`,
    [MeasureStatus.IMPOSSIBILITADO_PRISAO ||
    MeasureStatusLaPsc.IMPOSSIBILITADO_PRISAO]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorPrisao`,
    //
    [MeasureStatusLaPsc.CUMPRINDO_LA_CUMPRINDO_PSC]: 'Cumprindo LA e PSC',
    [MeasureStatusLaPsc.CUMPRINDO_LA_DESCUMPRINDO_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataInicioDescumprimentoPsc`,
    [MeasureStatusLaPsc.DESCUMPRINDO_LA_CUMPRINDO_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataInicioDescumprimentoLa`,
    [MeasureStatusLaPsc.EXTINTA_LA_CUMPRINDO_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSentencaExtincaoLa`,
    [MeasureStatusLaPsc.EXTINTA_PSC_CUMPRINDO_LA]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSentencaExtincaoPsc`,
    [MeasureStatusLaPsc.EXTINTA_LA_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataDesligamentoPorSentencaExtincaoLaPsc`,
    [MeasureStatusLaPsc.CUMPRINDO_LA_SUSPENSA_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataSentencaSuspensaoPsc`,
    [MeasureStatusLaPsc.SUSPENSA_LA_CUMPRINDO_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataSentencaSuspensaoLa`,
    [MeasureStatusLaPsc.SUSPENSA_LA_PSC]: `medidaSocioeducativa.${nameTipoSocioeducativa}.dataSentencaSuspensaoLaPsc`,
  }

  const labelMap: Record<MeasureStatus | MeasureStatusLaPsc, string> = {
    [MeasureStatus.CUMPRINDO]: '',
    [MeasureStatus.DESCUMPRINDO]: 'Data do Início do Desligamento por Descumprimento',
    [MeasureStatus.EXTINTA]: 'Data do Desligamento por Sentença de Extinção',
    [MeasureStatus.OBITO]: 'Data do Desligamento por Óbito',
    [MeasureStatus.SUSPENSA]: 'Data da Sentença',
    [MeasureStatus.TRANSFERENCIA_CREAS]: 'Data da Transferência',
    [MeasureStatus.TRANSFERENCIA_COMARCA]: 'Data do Desligamento por Transferência de Comarca',
    [MeasureStatus.SUBSTITUICAO_MEDIDA]: 'Data do Desligamento por Substituição de Medida',
    [MeasureStatus.IMPOSSIBILITADO_NOVA_APREENSAO]: 'Data do Desligamento por Nova Apreensão',
    [MeasureStatus.IMPOSSIBILITADO_PRISAO]: 'Data do Desligamento por Prisão',
    [MeasureStatusLaPsc.CUMPRINDO_LA_CUMPRINDO_PSC]: '',
    [MeasureStatusLaPsc.CUMPRINDO_LA_DESCUMPRINDO_PSC]:
      'Data de Cumprimento de LA, Descumprimento de PSC',
    [MeasureStatusLaPsc.DESCUMPRINDO_LA_CUMPRINDO_PSC]:
      'Data de Descumprimento de LA, Cumprimento de PSC',
    [MeasureStatusLaPsc.EXTINTA_LA_CUMPRINDO_PSC]: 'Data de Extinção de LA, Cumprimento de PSC',
    [MeasureStatusLaPsc.EXTINTA_PSC_CUMPRINDO_LA]: 'Data de Extinção de PSC, Cumprimento de LA',
    [MeasureStatusLaPsc.EXTINTA_LA_PSC]: 'Data de Extinção de LA e PSC',
    [MeasureStatusLaPsc.CUMPRINDO_LA_SUSPENSA_PSC]: 'Data de Cumprimento de LA, Suspensão de PSC',
    [MeasureStatusLaPsc.SUSPENSA_LA_CUMPRINDO_PSC]: 'Data de Suspensão de LA, Cumprimento de PSC',
    [MeasureStatusLaPsc.SUSPENSA_LA_PSC]: 'Data de Suspensão de LA e PSC',
  }

  const inputFileNameMap: Record<string, { base64: string; filename: string; filetype: string }> = {
    [MeasureStatus.CUMPRINDO]: { base64: '', filename: '', filetype: '' },
    [MeasureStatus.DESCUMPRINDO]: {
      base64: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseDescumprimento.arquivoBase64`,
      filename: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseDescumprimento.arquivoNome`,
      filetype: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseDescumprimento.arquivoTipo`,
    },
    [MeasureStatus.EXTINTA]: { base64: '', filename: '', filetype: '' },
    [MeasureStatus.OBITO || MeasureStatusLaPsc.OBITO]: {
      base64: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseObito.arquivoBase64`,
      filename: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseObito.arquivoNome`,
      filetype: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseObito.arquivoTipo`,
    },
    [MeasureStatus.SUSPENSA]: { base64: '', filename: '', filetype: '' },
    [MeasureStatus.TRANSFERENCIA_CREAS || MeasureStatusLaPsc.TRANSFERENCIA_CREAS]: {
      base64: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseTransferenciaCreas.arquivoBase64`,
      filename: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseTransferenciaCreas.arquivoNome`,
      filetype: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseTransferenciaCreas.arquivoTipo`,
    },
    [MeasureStatus.TRANSFERENCIA_COMARCA || MeasureStatusLaPsc.TRANSFERENCIA_COMARCA]: {
      base64: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseMudancaComarca.arquivoBase64`,
      filename: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseMudancaComarca.arquivoNome`,
      filetype: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseMudancaComarca.arquivoTipo`,
    },
    [MeasureStatus.SUBSTITUICAO_MEDIDA || MeasureStatusLaPsc.SUBSTITUICAO_MEDIDA]: {
      base64: '',
      filename: '',
      filetype: '',
    },
    [MeasureStatus.IMPOSSIBILITADO_NOVA_APREENSAO]: {
      base64: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseNovaApreensao.arquivoBase64`,
      filename: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseNovaApreensao.arquivoNome`,
      filetype: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSinteseNovaApreensao.arquivoTipo`,
    },
    [MeasureStatus.IMPOSSIBILITADO_PRISAO || MeasureStatusLaPsc.IMPOSSIBILITADO_PRISAO]: {
      base64: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSintesePrisao.arquivoBase64`,
      filename: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSintesePrisao.arquivoNome`,
      filetype: `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoSintesePrisao.arquivoTipo`,
    },
  }

  const defaultInputFileName = { base64: '', filename: '', filetype: '' }

  const inputFileNames = inputFileNameMap[measureStatusValue ?? ''] || defaultInputFileName
  const {
    address: { stateOptions, cityOptions },
  } = useAddress(
    tipoSocioeducativaValue === 'LA' || tipoSocioeducativaValue === undefined
      ? `${idEstadoWatchLa}`
      : tipoSocioeducativaValue === 'PSC' || tipoSocioeducativaValue === undefined
      ? `${idEstadoWatchPsc}`
      : tipoSocioeducativaValue === 'LA_PSC' || tipoSocioeducativaValue === undefined
      ? `${idEstadoWatchLaPsc}`
      : '0',
  )

  const disableAsDocumentTypeNotEntered = (): boolean => {
    if (disabled) return true
    return !measureStatusValue
  }
  useEffect(() => {
    if (tipoSocioeducativaValue === 'LA') {
      setValue('medidaSocioeducativa.la', measureStatusLaDTO)
      setValue(`medidaSocioeducativa.statusMedida`, measureStatusValue)
      setValue(`medidaSocioeducativa.la.estadoMoradiaDeclarado.nome`, '')
      setValue(`medidaSocioeducativa.la.municipioMoradiaDeclarado.nome`, '')
      setValue(`medidaSocioeducativa.la.municipioMoradiaDeclarado.uf`, { id: 0, nome: '' })
    } else if (tipoSocioeducativaValue === 'PSC') {
      setValue('medidaSocioeducativa.psc', measureStatusPscDTO)
      setValue(`medidaSocioeducativa.statusMedida`, measureStatusValue)
      setValue(`medidaSocioeducativa.psc.estadoMoradiaDeclarado.nome`, '')
      setValue(`medidaSocioeducativa.psc.municipioMoradiaDeclarado.nome`, '')
      setValue(`medidaSocioeducativa.psc.municipioMoradiaDeclarado.uf`, { id: 0, nome: '' })
    } else if (tipoSocioeducativaValue === 'LA_PSC') {
      setValue('medidaSocioeducativa.laPsc', measureStatusLaPscDTO)
      setValue(`medidaSocioeducativa.statusMedidaLaPsc`, measureStatusValueLaPsc)
      setValue(`medidaSocioeducativa.laPsc.estadoMoradiaDeclarado.nome`, '')
      setValue(`medidaSocioeducativa.laPsc.municipioMoradiaDeclarado.nome`, '')
      setValue(`medidaSocioeducativa.laPsc.municipioMoradiaDeclarado.uf`, { id: 0, nome: '' })
    }
  }, [tipoSocioeducativaValue, measureStatusValue, measureStatusValueLaPsc])

  useEffect(() => {
    !idEstadoWatchLa && setValue(`medidaSocioeducativa.la.municipioMoradiaDeclarado.id`, 0)
    !idEstadoWatchPsc && setValue(`medidaSocioeducativa.psc.municipioMoradiaDeclarado.id`, 0)
    !idEstadoWatchLaPsc && setValue(`medidaSocioeducativa.laPsc.municipioMoradiaDeclarado.id`, 0)
  }, [idEstadoWatchLa, idEstadoWatchPsc, idEstadoWatchLaPsc, setValue])

  return (
    <Paper variant='outlined' sx={{ p: 3, width: '100%', backgroundColor: 'white' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ flex: { xs: '1 0 100%', lg: '0 0 360px' } }}>
          <FormInputAutoComplete
            control={control}
            name={
              tipoSocioeducativaValue === 'LA_PSC'
                ? `medidaSocioeducativa.statusMedidaLaPsc`
                : `medidaSocioeducativa.statusMedida`
            }
            label='Status da Medida'
            options={(tipoSocioeducativaValue === 'LA_PSC' ? typeOptionsLaPsc : typeOptions).sort(
              (a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }),
            )}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            getOptionById={(id: string | null, options: ItemOption[]) =>
              options.find((option) => option.value === id) || null
            }
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            onChangeValue={(option) => option?.value || null}
          />
        </Box>

        {measureStatusValue &&
        measureStatusValue !== 'CUMPRINDO' &&
        measureStatusValue !== 'CUMPRINDO_LA_CUMPRINDO_PSC' ? (
          <>
            {(measureStatusValue === 'TRANSFERENCIA_CREAS' &&
              (tipoSocioeducativaValue === 'LA' || tipoSocioeducativaValue === 'PSC')) ||
            (measureStatusValueLaPsc === 'TRANSFERENCIA_CREAS' &&
              tipoSocioeducativaValue === 'LA_PSC') ? (
              <Box sx={{ flex: { xs: '1 0 100%', lg: '0 0 380px' } }}>
                <FormInputAutoComplete
                  label={'CREAS:'}
                  name={
                    tipoSocioeducativaValue === 'LA_PSC'
                      ? `medidaSocioeducativa.laPsc.creasTransferido`
                      : tipoSocioeducativaValue === 'LA'
                      ? `medidaSocioeducativa.la.creasTransferido`
                      : `medidaSocioeducativa.psc.creasTransferido`
                  }
                  control={control}
                  options={creas}
                  disabled={doNotHasEditPermission}
                  getOptionLabel={(option) => option.nome}
                  getOptionById={() =>
                    selectedWatch && selectedWatch.id && selectedWatch.nome
                      ? ({
                          id: selectedWatch.id,
                          nome: selectedWatch.nome,
                          abrangencia: [],
                          ativo: '',
                          diretor: '',
                          email: '',
                          endereco: addressInit,
                          telefones: [],
                        } as CreasResponse)
                      : null
                  }
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  onChangeValue={(option) => option || null}
                />
              </Box>
            ) : null}
            <Box sx={{ flex: { lg: '0 0 41%', xs: '1 0 100%' } }}>
              <FormInputDatePicker
                control={control}
                name={nameMap[measureStatusValue as MeasureStatus | MeasureStatusLaPsc] || ''}
                label={labelMap[measureStatusValue as MeasureStatus | MeasureStatusLaPsc] || ''}
                disabled={disabled}
              />
            </Box>
            <Box sx={{ flex: { lg: '0 0 75%', xs: '1 0 100%' } }}>
              <Stack
                spacing={1}
                direction={{ xs: 'column', lg: 'row' }}
                sx={{
                  gap: 2,
                }}
              >
                {measureStatusValue === 'TRANSFERENCIA_COMARCA' && (
                  <>
                    <Box sx={{ flex: { lg: '0 0 270px', xs: '1 0 100%' } }}>
                      <FormInputAutoComplete
                        control={control}
                        name={
                          tipoSocioeducativaValue === 'LA_PSC'
                            ? `medidaSocioeducativa.laPsc.estadoMoradiaDeclarado.id`
                            : tipoSocioeducativaValue === 'PSC'
                            ? `medidaSocioeducativa.psc.estadoMoradiaDeclarado.id`
                            : `medidaSocioeducativa.la.estadoMoradiaDeclarado.id`
                        }
                        label='Estado de Moradia Declarado'
                        options={stateOptions}
                        disabled={disabled}
                        getOptionLabel={(option) => option.label}
                        getOptionById={(id: string | null, options: ItemOption[]) =>
                          options.find((option) => Number(option.value) === Number(id)) || null
                        }
                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                        onChangeValue={(option) => Number(option?.value) || null}
                      />
                    </Box>
                    <Box sx={{ flex: { lg: '0 0 290px', xs: '1 0 100%' } }}>
                      <FormInputAutoComplete
                        control={control}
                        name={
                          tipoSocioeducativaValue === 'LA_PSC'
                            ? `medidaSocioeducativa.laPsc.municipioMoradiaDeclarado.id`
                            : tipoSocioeducativaValue === 'PSC'
                            ? `medidaSocioeducativa.psc.municipioMoradiaDeclarado.id`
                            : `medidaSocioeducativa.la.municipioMoradiaDeclarado.id`
                        }
                        label='Município de Moradia Declarado'
                        options={cityOptions}
                        disabled={
                          ((idEstadoWatchLa === 0 || idEstadoWatchLa === null) &&
                            tipoSocioeducativaValue === 'LA') ||
                          ((idEstadoWatchPsc === 0 || idEstadoWatchPsc === null) &&
                            tipoSocioeducativaValue === 'PSC') ||
                          ((idEstadoWatchLaPsc === 0 || idEstadoWatchLaPsc === null) &&
                            tipoSocioeducativaValue === 'LA_PSC') ||
                          (tipoSocioeducativaValue === undefined &&
                            (idEstadoWatchLa === 0 || idEstadoWatchLa === null) &&
                            (idEstadoWatchLaPsc === 0 || idEstadoWatchLaPsc === null) &&
                            isBlockedFields)
                        }
                        getOptionLabel={(option) => option.label}
                        getOptionById={(id: string | null, options: ItemOption[]) =>
                          options.find((option) => Number(option.value) === Number(id)) || null
                        }
                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                        onChangeValue={(option) => Number(option?.value) || null}
                      />
                    </Box>
                  </>
                )}
                {((tipoSocioeducativaValue === 'LA' ||
                  tipoSocioeducativaValue === 'PSC' ||
                  tipoSocioeducativaValue === undefined) &&
                  measureStatusValueLP !== 'EXTINTA' &&
                  measureStatusValueLP !== 'SUSPENSA' &&
                  measureStatusValueLP !== 'CUMPRINDO' &&
                  measureStatusValueLP !== 'SUBSTITUICAO_MEDIDA') ||
                ((tipoSocioeducativaValue === 'LA_PSC' || tipoSocioeducativaValue === undefined) &&
                  measureStatusValueLaPsc !== 'CUMPRINDO_LA_DESCUMPRINDO_PSC' &&
                  measureStatusValueLaPsc !== 'DESCUMPRINDO_LA_CUMPRINDO_PSC' &&
                  measureStatusValueLaPsc !== 'EXTINTA_LA_CUMPRINDO_PSC' &&
                  measureStatusValueLaPsc !== 'EXTINTA_PSC_CUMPRINDO_LA' &&
                  measureStatusValueLaPsc !== 'EXTINTA_LA_PSC' &&
                  measureStatusValueLaPsc !== 'CUMPRINDO_LA_SUSPENSA_PSC' &&
                  measureStatusValueLaPsc !== 'SUSPENSA_LA_CUMPRINDO_PSC' &&
                  measureStatusValueLaPsc !== 'SUSPENSA_LA_PSC' &&
                  measureStatusValueLaPsc !== 'IMPOSSIBILITADO_NOVA_APREENSAO' &&
                  measureStatusValueLaPsc !== 'SUBSTITUICAO_MEDIDA') ? (
                  <>
                    {(measureStatusValueLP === 'OBITO' &&
                      (tipoSocioeducativaValue === 'LA' ||
                        tipoSocioeducativaValue === 'PSC' ||
                        tipoSocioeducativaValue === undefined)) ||
                    (measureStatusValueLaPsc === 'OBITO' &&
                      (tipoSocioeducativaValue === 'LA_PSC' ||
                        tipoSocioeducativaValue === undefined)) ? (
                      <>
                        <Box
                          sx={{ flex: { lg: '0 0 10%', xs: '1 0 100%' } }}
                          alignSelf={{ lg: 'center' }}
                        >
                          <Label bold text='Síntese do óbito:' noWrap />
                        </Box>
                        <Box>
                          <FormInputFile
                            control={control}
                            name={
                              inputFileNames.base64 as Path<SocioeducationMeasureProceduralDataType>
                            }
                            nameFilename={
                              inputFileNames.filename as Path<SocioeducationMeasureProceduralDataType>
                            }
                            nameFiletype={
                              inputFileNames.filetype as Path<SocioeducationMeasureProceduralDataType>
                            }
                            setValue={setValue}
                            setError={setError}
                            clearErrors={clearErrors}
                            disabled={disableAsDocumentTypeNotEntered()}
                          />
                        </Box>
                        <Box
                          sx={{ flex: { lg: '0 0 10%', xs: '1 0 100%' } }}
                          alignSelf={{ lg: 'center' }}
                        >
                          <Label bold text='Certidão de óbito:' noWrap />
                        </Box>
                        <Box>
                          <FormInputFile
                            control={control}
                            name={
                              `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoCertidaoObito.arquivoBase64` as Path<SocioeducationMeasureProceduralDataType>
                            }
                            nameFilename={
                              `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoCertidaoObito.arquivoNome` as Path<SocioeducationMeasureProceduralDataType>
                            }
                            nameFiletype={
                              `medidaSocioeducativa.${nameTipoSocioeducativa}.anexoCertidaoObito.arquivoTipo` as Path<SocioeducationMeasureProceduralDataType>
                            }
                            setValue={setValue}
                            setError={setError}
                            clearErrors={clearErrors}
                            disabled={disableAsDocumentTypeNotEntered()}
                          />
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ flex: { lg: '0 0 10%', xs: '1 0 100%' } }}>
                        <FormInputFile
                          control={control}
                          name={
                            inputFileNames.base64 as Path<SocioeducationMeasureProceduralDataType>
                          }
                          nameFilename={
                            inputFileNames.filename as Path<SocioeducationMeasureProceduralDataType>
                          }
                          nameFiletype={
                            inputFileNames.filetype as Path<SocioeducationMeasureProceduralDataType>
                          }
                          setValue={setValue}
                          setError={setError}
                          clearErrors={clearErrors}
                          disabled={disableAsDocumentTypeNotEntered()}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  ''
                )}
              </Stack>
            </Box>
            {isRemovable && (
              <IconButton
                // onClick={() => onRemove(index)}
                sx={{ maxHeight: 40 }}
                disabled={disabled}
              >
                <DeleteOutlineRoundedIcon color='error' />
              </IconButton>
            )}
          </>
        ) : null}
      </Box>
    </Paper>
  )
}
